"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys, type QueryFilters } from "@/lib/query-keys";

// ── Carriers ─────────────────────────────────────────────────────────────────

export const useCarriersList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.carriers.list(filters),
    queryFn: () => api.carriers.list(filters as any),
    placeholderData: keepPreviousData,
  });
};

export const useCarrierDetail = (id?: string, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.carriers.detail(id || ""),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      if (!id) return undefined;
      return api.carriers.get(id);
    },
  });
};

export const useCreateCarrier = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.carriers.create>[0]) =>
      api.carriers.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carriers.lists() });
    },
  });
};

export const useUpdateCarrier = (id: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.carriers.update>[1]) =>
      api.carriers.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carriers.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.carriers.lists() });
    },
  });
};

// ── Carrier directions (nested) ──────────────────────────────────────────────

const carrierDirectionsKey = (carrierId: string, filters: QueryFilters = {}) =>
  [...queryKeys.carriers.detail(carrierId), "directions", filters] as const;

const carrierDirectionsRoot = (carrierId: string) =>
  [...queryKeys.carriers.detail(carrierId), "directions"] as const;

export const useCarrierDirections = (
  carrierId?: string,
  filters: QueryFilters = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: carrierDirectionsKey(carrierId || "", filters),
    enabled: Boolean(carrierId),
    queryFn: () => api.carriers.listDirections(carrierId as string, filters as any),
    placeholderData: keepPreviousData,
  });
};

export const useAddCarrierDirection = (carrierId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.carriers.addDirection>[1]) =>
      api.carriers.addDirection(carrierId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: carrierDirectionsRoot(carrierId) });
    },
  });
};

export const useUpdateCarrierDirection = (carrierId: string, directionId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.carriers.updateDirection>[2]) =>
      api.carriers.updateDirection(carrierId, directionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: carrierDirectionsRoot(carrierId) });
    },
  });
};
